// PreviewTester/components/SkillFieldsMapper.js
//
// Parallel to ActionFieldsBuilder, but for Skill (Intent) required_fields.
//
// IMPORTANT: Skill field mapping is OPTIONAL. The user may choose to prefill
// none, some, or all of the skill's declared fields. `required: true` on a
// skill field is a runtime contract of the skill itself — it says nothing
// about whether the admin must map it here.

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';

// ------------------------------------------------------------
// Same template grammar as ActionFieldsBuilder.
// ------------------------------------------------------------
const isValidTemplate = (value) => {
  if (value === null || value === undefined) return true;
  const str = String(value);
  if (str.trim() === '') return true;

  let i = 0;
  const len = str.length;
  while (i < len) {
    const openIdx = str.indexOf('{{', i);
    if (openIdx === -1) break;

    const closeIdx = str.indexOf('}}', openIdx + 2);
    if (closeIdx === -1) return false;

    const inner = str.slice(openIdx + 2, closeIdx);
    if (inner.includes('{{')) return false;

    const trimmedInner = inner.trim();
    if (trimmedInner === '') return false;

    const pathRegex = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/;
    if (!pathRegex.test(trimmedInner)) return false;

    i = closeIdx + 2;
  }

  let depth = 0;
  for (let j = 0; j < str.length; j++) {
    if (str[j] === '{' && str[j + 1] === '{') {
      depth++;
      j++;
    } else if (str[j] === '}' && str[j + 1] === '}') {
      depth--;
      if (depth < 0) return false;
      j++;
    }
  }
  return depth === 0;
};

// ------------------------------------------------------------
// ⭐ NEW: Normalize a mapping entry to the new object shape.
//
// Accepts:
//   • string  → legacy: treat as value with default flags
//   • object  → new: merge with default flags
//
// Defaults:
//   prefill     = true  (only meaningful when value is non-empty)
//   read_only   = false
//   display     = true
// ------------------------------------------------------------
const normalizeMappingEntry = (entry) => {
  const DEFAULT = {
    value: '',
    prefill: true,
    read_only: false,
    display: true,
  };

  if (entry === null || entry === undefined) return { ...DEFAULT };

  if (typeof entry === 'string') {
    return { ...DEFAULT, value: entry };
  }

  if (typeof entry === 'object') {
    return {
      value: typeof entry.value === 'string' ? entry.value : '',
      prefill: entry.prefill !== undefined ? !!entry.prefill : true,
      read_only: entry.read_only !== undefined ? !!entry.read_only : false,
      display: entry.display !== undefined ? !!entry.display : true,
    };
  }

  return { ...DEFAULT };
};

// ------------------------------------------------------------
// Cheap type hint (never blocks a save).
// ------------------------------------------------------------
const inferResponseFieldType = (fieldPath) => {
  const lower = String(fieldPath || '').toLowerCase();
  if (/(^|\.)(id|_id|count|qty|quantity|age|number|num|amount|total)\b/.test(lower))
    return 'number';
  if (/(^|\.)(date|_at|_on|time|timestamp)\b/.test(lower)) return 'date';
  if (/(^|\.)(is_|has_|can_|should_|enabled|active|flag)\b/.test(lower))
    return 'boolean';
  return 'unknown';
};

const typeMatches = (skillType, responseType) => {
  if (responseType === 'unknown') return true;
  if (skillType === responseType) return true;
  if (
    (skillType === 'number' || skillType === 'integer' || skillType === 'float') &&
    (responseType === 'number' || responseType === 'integer' || responseType === 'float')
  )
    return true;
  return false;
};

// ------------------------------------------------------------
// Deep-ish equality for the mapping object (now contains nested
// objects per field). Compares value + all three flags.
// ------------------------------------------------------------
const mappingsEqual = (a, b) => {
  const ak = Object.keys(a || {});
  const bk = Object.keys(b || {});
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    const av = normalizeMappingEntry(a[k]);
    const bv = normalizeMappingEntry(b[k]);
    if (
      av.value !== bv.value ||
      av.prefill !== bv.prefill ||
      av.read_only !== bv.read_only ||
      av.display !== bv.display
    ) {
      return false;
    }
  }
  return true;
};

// PreviewTester/components/SkillFieldsMapper.js
//
// (…rest of the file above, unchanged…)

// ------------------------------------------------------------
// ⭐ NEW: Helpers for the MappingExplainer.
// ------------------------------------------------------------
const pickItemExample = (availableFields = []) => {
  const itemFields = availableFields.filter((f) => f.startsWith('item.'));
  if (itemFields.length === 0) return null;

  const preferred = ['name', 'title', 'label', 'room_number', 'email'];
  for (const key of preferred) {
    const hit = itemFields.find((f) => f.toLowerCase().endsWith(`.${key}`));
    if (hit) return hit;
  }
  return itemFields[0];
};

const pickScreenExample = (availableFields = []) => {
  const screenFields = availableFields.filter((f) =>
    f.startsWith('response_data.'),
  );
  if (screenFields.length === 0) return null;

  const preferred = ['name', 'title', 'currency', 'city', 'location'];
  for (const key of preferred) {
    const hit = screenFields.find((f) => f.toLowerCase().endsWith(`.${key}`));
    if (hit) return hit;
  }
  return screenFields[0];
};

// ------------------------------------------------------------
// MappingExplainer — unchanged from your current version.
// ------------------------------------------------------------
export const MappingExplainer = ({ tone = 'default', availableFields = [] }) => {
  const [expanded, setExpanded] = React.useState(false);

  const accent = tone === 'skill' ? '#805ad5' : '#4299e1';
  const bg = tone === 'skill' ? '#faf5ff' : '#f0f4ff';
  const border = tone === 'skill' ? '#d6bcfa' : '#bee3f8';

  const itemExample = pickItemExample(availableFields);
  const screenExample = pickScreenExample(availableFields);

  const hasItemExample = !!itemExample;
  const hasScreenExample = !!screenExample;

  return (
    <div
      style={{
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: '6px',
        padding: '10px 12px',
        marginBottom: '12px',
        fontSize: '12px',
        color: '#2d3748',
        lineHeight: '1.55',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '10px',
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: '600',
              color: accent,
              fontSize: '12px',
              marginBottom: '4px',
            }}
          >
            💡 What do these fields mean?
          </div>
          <div>
            Each screen is built from a <b>response</b> — the JSON your API
            sent back. Your template draws <i>some</i> of those values on
            the screen. Fields that aren't drawn are still available here.
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            padding: '3px 10px',
            backgroundColor: 'white',
            border: `1px solid ${accent}55`,
            color: accent,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
          }}
        >
          {expanded ? 'Hide details' : 'Learn more'}
        </button>
      </div>

      {expanded && (
        <div
          style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: `1px dashed ${border}`,
            lineHeight: '1.65',
          }}
        >
          <div style={{ marginBottom: '10px' }}>
            When you map a field, you're telling the button:{' '}
            <i>"use the value from this field when I'm clicked."</i> You can
            pick from two places:
          </div>

          {hasItemExample ? (
            <div style={{ marginBottom: '10px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '2px',
                }}
              >
                <span
                  style={{
                    padding: '1px 8px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#2b6cb0',
                    backgroundColor: '#ebf8ff',
                    fontFamily: 'monospace',
                  }}
                >
                  item
                </span>
                <span style={{ fontWeight: '600' }}>
                  The fields of this row
                </span>
              </div>
              <div style={{ paddingLeft: '4px', color: '#4a5568' }}>
                Use these when the button lives inside a repeating section
                (a list). Each row has its own value, and the button picks
                the value of the row the user actually clicked.
              </div>
              <div
                style={{
                  marginTop: '4px',
                  paddingLeft: '4px',
                  fontSize: '11px',
                  color: '#718096',
                  fontFamily: 'monospace',
                }}
              >
                e.g.{' '}
                <code
                  style={{
                    backgroundColor: '#edf2f7',
                    padding: '1px 6px',
                    borderRadius: '4px',
                  }}
                >
                  {`{{ ${itemExample} }}`}
                </code>
              </div>
            </div>
          ) : (
            <div
              style={{
                marginBottom: '10px',
                paddingLeft: '4px',
                color: '#718096',
                fontStyle: 'italic',
              }}
            >
              <b>item</b> — the fields of the current row. Only available
              when the button lives inside a repeating section.
            </div>
          )}

          {hasScreenExample ? (
            <div style={{ marginBottom: '10px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '2px',
                }}
              >
                <span
                  style={{
                    padding: '1px 8px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#2b6cb0',
                    backgroundColor: '#ebf8ff',
                    fontFamily: 'monospace',
                  }}
                >
                  response_data
                </span>
                <span style={{ fontWeight: '600' }}>
                  The fields of this screen
                </span>
              </div>
              <div style={{ paddingLeft: '4px', color: '#4a5568' }}>
                Use these for values that describe the whole screen — the
                same for every row. Property name, currency, city, and so
                on.
              </div>
              <div
                style={{
                  marginTop: '4px',
                  paddingLeft: '4px',
                  fontSize: '11px',
                  color: '#718096',
                  fontFamily: 'monospace',
                }}
              >
                e.g.{' '}
                <code
                  style={{
                    backgroundColor: '#edf2f7',
                    padding: '1px 6px',
                    borderRadius: '4px',
                  }}
                >
                  {`{{ ${screenExample} }}`}
                </code>
              </div>
            </div>
          ) : (
            <div
              style={{
                marginBottom: '10px',
                paddingLeft: '4px',
                color: '#718096',
                fontStyle: 'italic',
              }}
            >
              <b>response_data</b> — the fields that describe the whole
              screen.
            </div>
          )}

          <div
            style={{
              marginTop: '10px',
              paddingTop: '10px',
              borderTop: `1px dashed ${border}`,
              color: '#4a5568',
              fontStyle: 'italic',
            }}
          >
            Some fields in the dropdown aren't drawn anywhere on the screen.
            That's normal — the response contains more than the template
            shows. You can still map to them; the client will look them up
            at click time.
          </div>
        </div>
      )}
    </div>
  );
};


const SkillFieldsMapper = ({
  skillFields = [],
  availableFields = [],
  onMappingChange,
  readOnly = false,
  initialMappings = {},
}) => {
  // ⭐ Internal state now stores the object form for every field.
  const [mappings, setMappings] = useState({});
  const [showTemplateEditor, setShowTemplateEditor] = useState({});
  const inputRefs = useRef({});

  const lastEmittedRef = useRef({
    mappings: null,
    allValid: null,
  });

  const isSyncingFromPropsRef = useRef(false);

  const skillFieldsSignature = useMemo(
    () => skillFields.map((f) => f.field).join('|'),
    [skillFields],
  );

  // ⭐ Signature now reflects the FULL object form (value + flags).
  const initialMappingsSignature = useMemo(
    () =>
      Object.keys(initialMappings || {})
        .sort()
        .map((k) => {
          const e = normalizeMappingEntry(initialMappings[k]);
          return `${k}=${e.value}|${e.prefill ? 1 : 0}${e.read_only ? 1 : 0}${e.display ? 1 : 0}`;
        })
        .join('|'),
    [initialMappings],
  );

  useEffect(() => {
    const next = {};
    skillFields.forEach((f) => {
      // ⭐ Normalize whatever came in (string or object) to the
      //    canonical object shape.
      next[f.field] = normalizeMappingEntry(initialMappings?.[f.field]);
    });

    if (mappingsEqual(mappings, next)) {
      return;
    }

    isSyncingFromPropsRef.current = true;
    setMappings(next);
    setShowTemplateEditor({});
  }, [skillFieldsSignature, initialMappingsSignature, initialMappings]);

  const allMappingsValid = useMemo(() => {
    for (const e of Object.values(mappings)) {
      const entry = normalizeMappingEntry(e);
      if (!isValidTemplate(entry.value)) return false;
    }
    return true;
  }, [mappings]);

  useEffect(() => {
    if (!onMappingChange) return;

    if (isSyncingFromPropsRef.current) {
      isSyncingFromPropsRef.current = false;
      lastEmittedRef.current = {
        mappings: { ...mappings },
        allValid: allMappingsValid,
      };
      return;
    }

    const last = lastEmittedRef.current;
    const sameMappings =
      last.mappings && mappingsEqual(last.mappings, mappings);
    const sameValidity = last.allValid === allMappingsValid;

    if (sameMappings && sameValidity) return;

    lastEmittedRef.current = {
      mappings: { ...mappings },
      allValid: allMappingsValid,
    };

    onMappingChange(mappings, allMappingsValid);
  }, [mappings, allMappingsValid, onMappingChange]);

  // ⭐ Update the whole entry, or just one field of it.
  const handleEntryUpdate = useCallback((skillField, patch) => {
    setMappings((prev) => {
      const current = normalizeMappingEntry(prev[skillField]);
      const next = { ...current, ...patch };
      if (
        current.value === next.value &&
        current.prefill === next.prefill &&
        current.read_only === next.read_only &&
        current.display === next.display
      ) {
        return prev;
      }
      return { ...prev, [skillField]: next };
    });
  }, []);

  // Convenience: value-only update (used by the text input).
  const handleValueUpdate = useCallback(
    (skillField, value) => handleEntryUpdate(skillField, { value }),
    [handleEntryUpdate],
  );

  const availableFieldsWithLabels = useMemo(
    () =>
      (availableFields || []).map((f) => ({
        value: f,
        label: f.replace(/\./g, ' → '),
        inferredType: inferResponseFieldType(f),
      })),
    [availableFields],
  );

  const toggleTemplateEditor = useCallback((fieldKey) => {
    setShowTemplateEditor((prev) => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  }, []);

  const insertFieldIntoTemplate = useCallback(
    (fieldItem, currentValue, skillField) => {
      const key = `skill_${skillField}`;
      const inputRef = inputRefs.current[key];
      const cursorPos = inputRef?.selectionStart ?? (currentValue || '').length;
      const before = (currentValue || '').substring(0, cursorPos);
      const after = (currentValue || '').substring(cursorPos);
      const newValue = before + `{{ ${fieldItem} }}` + after;

      handleValueUpdate(skillField, newValue);
      setShowTemplateEditor((prev) => ({ ...prev, [key]: false }));

      setTimeout(() => {
        if (inputRef) {
          inputRef.focus();
          const pos = cursorPos + `{{ ${fieldItem} }}`.length;
          inputRef.setSelectionRange(pos, pos);
        }
      }, 10);
    },
    [handleValueUpdate],
  );

  if (!skillFields || skillFields.length === 0) {
    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: '#f7fafc',
          borderRadius: '8px',
          border: '1px dashed #e2e8f0',
          textAlign: 'center',
          color: '#a0aec0',
          fontSize: '13px',
        }}
      >
        This skill does not declare any form fields.
      </div>
    );
  }

  if (availableFieldsWithLabels.length === 0) {
    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: '#fefcbf',
          borderRadius: '8px',
          border: '1px solid #f6ad55',
          textAlign: 'center',
          color: '#744210',
          fontSize: '13px',
        }}
      >
        ⚠️ No response fields available for mapping.
        <br />
        <span style={{ fontSize: '12px' }}>
          Please render a preview first to see available fields from your
          sample data.
        </span>
      </div>
    );
  }

  const mappedCount = skillFields.filter((f) => {
    const e = normalizeMappingEntry(mappings[f.field]);
    return e.value && e.value.trim() !== '';
  }).length;
  const totalFields = skillFields.length;

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        padding: '16px',
        marginTop: '12px',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <div>
          <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '13px' }}>
            🎯 Skill Field Prefill (optional)
          </span>
          <span style={{ marginLeft: '12px', fontSize: '11px', color: '#718096' }}>
            {mappedCount}/{totalFields} mapped
          </span>
        </div>
        <span
          style={{
            fontSize: '10px',
            color: '#718096',
            backgroundColor: '#edf2f7',
            padding: '2px 8px',
            borderRadius: '8px',
          }}
        >
          💡 Use {'{'} {'{'} field {'}'} {'}'} syntax
        </span>
      </div>

      {/* Informational note — no gating */}
      <div
        style={{
          fontSize: '11px',
          color: '#4a5568',
          backgroundColor: '#f0f4ff',
          border: '1px solid #bee3f8',
          borderRadius: '4px',
          padding: '6px 10px',
          marginBottom: '10px',
        }}
      >
        These mappings are <b>optional</b>. Any field you leave blank will not
        be prefilled on the client — the user will fill it in manually.
      </div>

      {/* Explainer banner */}
      <MappingExplainer tone="skill" availableFields={availableFields} />

      {/* Per-field rows */}
      {skillFields.map((f) => {
        const key = f.field;
        const entry = normalizeMappingEntry(mappings[key]);
        const value = entry.value;
        const isValid = isValidTemplate(value);
        const hasValue = value && value.trim() !== '';
        const showError = hasValue && !isValid;
        const isNotMapped = !hasValue;

        const inferred = inferResponseFieldType(value);
        const skillType = (f.type || '').toLowerCase();
        const typeWarning =
          hasValue &&
          !showError &&
          !typeMatches(skillType, inferred) &&
          inferred !== 'unknown';

        const borderColor = showError
          ? '#e53e3e'
          : hasValue && isValid
            ? '#38a169'
            : '#e2e8f0';
        const backgroundColor = showError
          ? '#fff5f5'
          : hasValue && isValid
            ? '#f0fff4'
            : 'white';

        const inputKey = `skill_${key}`;
        const dropdownOpen = showTemplateEditor[inputKey] || false;

        // Flag state for this row.
        const prefill = entry.prefill;
        const readOnlyFlag = entry.read_only;
        const displayFlag = entry.display;

        // When prefill is off, read_only is meaningless. Visual
        // cue: grey out the toggle so the user sees why.
        const readOnlyDisabled = !prefill;

        return (
          <div
            key={key}
            style={{
              padding: '8px 10px',
              marginBottom: '8px',
              backgroundColor: showError
                ? '#fff5f5'
                : hasValue
                  ? '#f0fff4'
                  : 'white',
              borderRadius: '6px',
              border: `1px solid ${showError ? '#fed7d7' : hasValue ? '#c6f6d5' : '#e2e8f0'
                }`,
              position: 'relative',
              zIndex: dropdownOpen ? 40 : 'auto',
            }}
          >
            {/* Field header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '6px',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontWeight: '600',
                  color: '#2d3748',
                  fontSize: '12px',
                }}
              >
                {f.label || f.field}
              </span>
              <code
                style={{
                  fontSize: '10px',
                  color: '#4a5568',
                  backgroundColor: '#edf2f7',
                  padding: '1px 6px',
                  borderRadius: '6px',
                }}
              >
                {f.field}
              </code>
              <span
                style={{
                  fontSize: '9px',
                  color: '#553c9a',
                  backgroundColor: '#faf5ff',
                  padding: '1px 6px',
                  borderRadius: '8px',
                }}
              >
                {f.type}
                {f.format ? ` · ${f.format}` : ''}
              </span>
              {f.required && (
                <span
                  style={{
                    fontSize: '9px',
                    color: '#718096',
                    backgroundColor: '#edf2f7',
                    padding: '1px 6px',
                    borderRadius: '8px',
                  }}
                  title="Required by the skill at runtime, but optional to prefill here"
                >
                  required by skill
                </span>
              )}
              {isNotMapped && (
                <span
                  style={{
                    fontSize: '9px',
                    color: '#a0aec0',
                    marginLeft: 'auto',
                    fontStyle: 'italic',
                  }}
                >
                  (not prefilled)
                </span>
              )}
            </div>

            {/* Input row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  flex: 1,
                  position: 'relative',
                  zIndex: dropdownOpen ? 50 : 'auto',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    ref={(el) => {
                      inputRefs.current[inputKey] = el;
                    }}
                    type="text"
                    value={value}
                    readOnly={readOnly}
                    onChange={(e) => handleValueUpdate(key, e.target.value)}
                    placeholder="e.g., {{ item.room_id }} or (leave blank to skip)"
                    style={{
                      flex: 1,
                      padding: '4px 8px',
                      border: `2px solid ${borderColor}`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      backgroundColor,
                      outline: 'none',
                      transition: 'border-color 0.2s, background-color 0.2s',
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow =
                        '0 0 0 2px rgba(128, 90, 213, 0.35)';
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      setTimeout(() => {
                        setShowTemplateEditor((prev) => ({
                          ...prev,
                          [inputKey]: false,
                        }));
                      }, 200);
                    }}
                  />
                  {!readOnly && (
                    <button
                      onClick={() => toggleTemplateEditor(inputKey)}
                      style={{
                        padding: '2px 8px',
                        backgroundColor: dropdownOpen ? '#805ad5' : '#9f7aea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        whiteSpace: 'nowrap',
                      }}
                      title="Insert field into template"
                    >
                      {dropdownOpen ? '✕ Close' : '+ Field'}
                    </button>
                  )}
                  {hasValue && !readOnly && (
                    <button
                      onClick={() => handleValueUpdate(key, '')}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#fc8181',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '0 4px',
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {showError && (
                  <div
                    style={{
                      marginTop: '2px',
                      fontSize: '10px',
                      color: '#c53030',
                      display: 'flex',
                      gap: '6px',
                      alignItems: 'center',
                    }}
                  >
                    <span>⚠️</span>
                    <span>
                      Invalid template. Use <code>{'{{ field.path }}'}</code>{' '}
                      syntax with balanced braces and valid identifiers.
                    </span>
                  </div>
                )}

                {typeWarning && (
                  <div
                    style={{
                      marginTop: '2px',
                      fontSize: '10px',
                      color: '#dd6b20',
                      display: 'flex',
                      gap: '6px',
                      alignItems: 'center',
                    }}
                  >
                    <span>ℹ️</span>
                    <span>
                      Skill expects <b>{f.type}</b>
                      {f.format ? ` (${f.format})` : ''}, but the mapped field
                      looks like <b>{inferred}</b>. The runtime will attempt
                      conversion.
                    </span>
                  </div>
                )}

                {dropdownOpen && !readOnly && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 100,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      marginTop: '4px',
                    }}
                  >
                    <div
                      style={{
                        padding: '6px 10px',
                        fontSize: '10px',
                        color: '#718096',
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor: '#f7fafc',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Click a field to insert it at cursor position
                    </div>
                    {availableFieldsWithLabels.map((fieldItem) => {
                      const compatible = typeMatches(
                        skillType,
                        fieldItem.inferredType,
                      );
                      return (
                        <div
                          key={fieldItem.value}
                          onClick={() =>
                            insertFieldIntoTemplate(fieldItem.value, value, key)
                          }
                          style={{
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            borderBottom: '1px solid #f7fafc',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = '#f0f4ff')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = 'white')
                          }
                        >
                          <span>{fieldItem.label}</span>
                          <span
                            style={{
                              fontSize: '9px',
                              color: compatible ? '#38a169' : '#dd6b20',
                              backgroundColor: compatible
                                ? '#f0fff4'
                                : '#fffaf0',
                              padding: '1px 6px',
                              borderRadius: '8px',
                            }}
                          >
                            {compatible ? 'Insert' : `type ~ ${skillType}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {hasValue && isValid && value.includes('{{') && (
                  <div
                    style={{
                      marginTop: '2px',
                      fontSize: '9px',
                      color: '#38a169',
                      fontStyle: 'italic',
                      display: 'flex',
                      gap: '12px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>📝 Template</span>
                    <span>
                      Fields:{' '}
                      {(value.match(/\{\{\s*([^}]+)\s*\}\}/g) || [])
                        .map((m) => m.replace(/[\{\}\s]/g, ''))
                        .join(', ')}
                    </span>
                  </div>
                )}

                {/* ⭐ NEW: three per-field toggles. */}
                <div
                  style={{
                    marginTop: '6px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    alignItems: 'center',
                    fontSize: '11px',
                    color: '#4a5568',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                      opacity: readOnly ? 0.6 : 1,
                    }}
                    title="Send this field's value to the client so the form opens with it already filled in."
                  >
                    <input
                      type="checkbox"
                      checked={prefill}
                      disabled={readOnly}
                      onChange={(e) =>
                        handleEntryUpdate(key, { prefill: e.target.checked })
                      }
                      style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                    />
                    Prefill
                  </label>

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      cursor: readOnlyDisabled ? 'not-allowed' : 'pointer',
                      opacity: readOnlyDisabled ? 0.5 : 1,
                    }}
                    title={
                      readOnlyDisabled
                        ? 'Enable Prefill first — a locked empty field cannot be filled by the user.'
                        : "Lock the field so the user can't change the prefilled value."
                    }
                  >
                    <input
                      type="checkbox"
                      checked={readOnlyFlag}
                      disabled={readOnly || readOnlyDisabled}
                      onChange={(e) =>
                        handleEntryUpdate(key, { read_only: e.target.checked })
                      }
                      style={{
                        cursor: readOnlyDisabled ? 'not-allowed' : 'pointer',
                      }}
                    />
                    Read-only
                  </label>

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                      opacity: readOnly ? 0.6 : 1,
                    }}
                    title="Hide this field from the form entirely. The prefilled value is still submitted."
                  >
                    <input
                      type="checkbox"
                      checked={displayFlag}
                      disabled={readOnly}
                      onChange={(e) =>
                        handleEntryUpdate(key, { display: e.target.checked })
                      }
                      style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                    />
                    Display
                  </label>
                </div>

                {/* ⭐ Hint about nonsensical combinations. */}
                {!prefill && readOnlyFlag && (
                  <div
                    style={{
                      marginTop: '4px',
                      fontSize: '10px',
                      color: '#dd6b20',
                      display: 'flex',
                      gap: '6px',
                      alignItems: 'center',
                    }}
                  >
                    <span>⚠️</span>
                    <span>
                      Read-only without prefill means the user sees a locked
                      empty field. Consider unchecking Read-only.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Summary — informational only */}
      <div
        style={{
          marginTop: '8px',
          padding: '8px 12px',
          backgroundColor: !allMappingsValid ? '#fff5f5' : '#f7fafc',
          borderRadius: '4px',
          fontSize: '11px',
          color: !allMappingsValid ? '#9b2c2c' : '#4a5568',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
          border: `1px solid ${!allMappingsValid ? '#fed7d7' : '#e2e8f0'}`,
        }}
      >
        <span>
          <strong>Prefill:</strong> {mappedCount} of {totalFields} fields
          mapped <span style={{ color: '#a0aec0' }}>(optional)</span>
        </span>
        {!allMappingsValid ? (
          <span style={{ color: '#c53030', fontWeight: '600' }}>
            ⚠️ Fix invalid templates
          </span>
        ) : (
          mappedCount > 0 && (
            <span style={{ color: '#38a169', fontWeight: '600' }}>✅ Ready</span>
          )
        )}
      </div>
    </div>
  );
};

export default SkillFieldsMapper;